import {
  AddLiveReportViewersInput,
  CreateLiveReportLinkInput,
  LiveReportLink,
  UpdateLiveReportLinkInput,
} from "../../models/live-report";

function getApiBaseUrl(): string {
  const baseUrl = process.env.NEXT_PUBLIC_TIXOLOGI_API_URL;
  if (!baseUrl) {
    throw new Error("NEXT_PUBLIC_TIXOLOGI_API_URL not configured");
  }
  return baseUrl;
}

/**
 * Optional callback to obtain a fresh Tixologi token when the cached one
 * comes back as expired (401/403). Returning null means "no fresh token
 * available" and the original error is propagated.
 */
export type TokenRefresher = () => Promise<string | null>;

async function doFetch(
  path: string,
  token: string,
  init: RequestInit
): Promise<Response> {
  const baseUrl = getApiBaseUrl();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (init.body !== undefined && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  return fetch(`${baseUrl}${path}`, { ...init, headers });
}

async function ticketingFetch<T>(
  path: string,
  token: string,
  init: RequestInit = {},
  refreshToken?: TokenRefresher
): Promise<T> {
  let response = await doFetch(path, token, init);

  // If the cached token is stale, give the caller one shot at refreshing it.
  if ((response.status === 401 || response.status === 403) && refreshToken) {
    const fresh = await refreshToken();
    if (fresh && fresh !== token) {
      response = await doFetch(path, fresh, init);
    }
  }

  if (!response.ok) {
    const body = await response.text();
    throw new Error(
      `Ticketing API ${init.method ?? "GET"} ${path} failed: ${response.status} ${body}`
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function listManagedLiveReportLinks(
  token: string,
  refreshToken?: TokenRefresher
): Promise<LiveReportLink[]> {
  return ticketingFetch<LiveReportLink[]>(
    "/ticketing/live-report/links",
    token,
    {},
    refreshToken
  );
}

export async function getManagedLiveReportLink(
  token: string,
  id: number,
  refreshToken?: TokenRefresher
): Promise<LiveReportLink> {
  return ticketingFetch<LiveReportLink>(
    `/ticketing/live-report/links/${id}`,
    token,
    {},
    refreshToken
  );
}

export async function createManagedLiveReportLink(
  token: string,
  input: CreateLiveReportLinkInput,
  refreshToken?: TokenRefresher
): Promise<LiveReportLink> {
  return ticketingFetch<LiveReportLink>(
    "/ticketing/live-report/links",
    token,
    { method: "POST", body: JSON.stringify(input) },
    refreshToken
  );
}

export async function updateManagedLiveReportLink(
  token: string,
  id: number,
  patch: UpdateLiveReportLinkInput,
  refreshToken?: TokenRefresher
): Promise<LiveReportLink> {
  return ticketingFetch<LiveReportLink>(
    `/ticketing/live-report/links/${id}`,
    token,
    { method: "PUT", body: JSON.stringify(patch) },
    refreshToken
  );
}

export async function revokeManagedLiveReportLink(
  token: string,
  id: number,
  refreshToken?: TokenRefresher
): Promise<void> {
  await ticketingFetch<void>(
    `/ticketing/live-report/links/${id}`,
    token,
    { method: "DELETE" },
    refreshToken
  );
}

export async function addManagedLiveReportViewers(
  token: string,
  id: number,
  emails: string[],
  refreshToken?: TokenRefresher
): Promise<LiveReportLink> {
  const body: AddLiveReportViewersInput = { emails };
  return ticketingFetch<LiveReportLink>(
    `/ticketing/live-report/links/${id}/viewers`,
    token,
    { method: "POST", body: JSON.stringify(body) },
    refreshToken
  );
}

export async function removeManagedLiveReportViewer(
  token: string,
  id: number,
  email: string,
  refreshToken?: TokenRefresher
): Promise<void> {
  await ticketingFetch<void>(
    `/ticketing/live-report/links/${id}/viewers/${encodeURIComponent(email)}`,
    token,
    { method: "DELETE" },
    refreshToken
  );
}
